export interface AppEvent {
  id: string;
  title: string;
  start_time: string;
  end_time: string;
  description?: string;
  location?: string;
  htmlLink?: string;
}

interface GoogleCalendarEvent {
  id: string;
  summary?: string;
  description?: string;
  location?: string;
  htmlLink?: string;
  start?: {
    dateTime?: string;
    date?: string;
  };
  end?: {
    dateTime?: string;
    date?: string;
  };
}

interface GoogleCalendarResponse {
  items?: GoogleCalendarEvent[];
}

const API_KEY = process.env.REACT_APP_GOOGLE_CALENDAR_API_KEY;
const CALENDAR_ID = process.env.REACT_APP_GOOGLE_CALENDAR_ID;

const buildCalendarUrl = (timeMin: Date, timeMax: Date): string => {
  if (!API_KEY || !CALENDAR_ID) {
    throw new Error('Missing Google Calendar API key or calendar ID.');
  }

  const params = new URLSearchParams({
    key: API_KEY,
    singleEvents: 'true',
    orderBy: 'startTime',
    timeMin: timeMin.toISOString(),
    timeMax: timeMax.toISOString()
  });

  return `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(CALENDAR_ID)}/events?${params.toString()}`;
};

const toIso = (value?: string): string => {
  if (!value) return new Date().toISOString();
  return new Date(value).toISOString();
};

const mapGoogleEvent = (event: GoogleCalendarEvent): AppEvent => {
  return {
    id: event.id,
    title: event.summary || 'Private Event',
    start_time: toIso(event.start?.dateTime || event.start?.date),
    end_time: toIso(event.end?.dateTime || event.end?.date),
    description: event.description,
    location: event.location,
    htmlLink: event.htmlLink
  };
};

export const getEvents = async (timeMin: Date, timeMax: Date): Promise<AppEvent[]> => {
  const url = buildCalendarUrl(timeMin, timeMax);
  const response = await fetch(url);
  if (!response.ok) {
    let details = '';
    try {
      const errorBody = await response.json();
      details = errorBody?.error?.message ? `: ${errorBody.error.message}` : '';
    } catch {
      // Ignore parsing errors and fall back to status-only message.
    }
    throw new Error(`Google Calendar request failed (${response.status})${details}`);
  }
  const data: GoogleCalendarResponse = await response.json();
  return (data.items || []).map(mapGoogleEvent);
};

